import axios from "axios";
import { ShowDangerToast } from "./toast";

const APP_URL = "https://d2uba4l2zex03h.cloudfront.net/api";
const CACHE_TTL = 4 * 60 * 1000;
let apiCache = new Map();

const getCacheKey = (url, params) => {
  const paramString = params ? JSON.stringify(params) : '';
  return `${url}|${paramString}`;
};

const isCacheValid = (cached) => {
  return cached && (Date.now() - cached.timestamp) < CACHE_TTL;
};

export default class ApiServices {
  static controllers = new Map();

  static invalidateCache() {
    apiCache.clear();
  }

  static fetchData = async (url, options = {}, params = {}) => {
    if (this.controllers.has(url)) {
      const controller = this.controllers.get(url);
      controller.abort();
      this.controllers.delete(url);
    }

    const controller = new AbortController();
    this.controllers.set(url, controller);

    try {
      const result = await axios.get(APP_URL + url, {
        params,
        headers: { ...options, Authorization: `Bearer ${localStorage.getItem('token')}` },
        signal: controller.signal,
      });
      this.controllers.delete(url);
      return result;
    } catch (error) {
      this.controllers.delete(url);
      if (error.name === "CanceledError") return;
      if (error.response?.status === 401) {
        if (url.includes('/auth') || url.includes('/stores')) {
          localStorage.clear();
          localStorage.setItem("issue", "You are not authorized...");
          window.location.href = "/";
        } else {
          ShowDangerToast("Unauthorized access. Please check your session.");
          throw error;
        }
      } else if (error.response?.status !== 200) {
        ShowDangerToast(error?.response?.data?.error || "Something went wrong!");
        throw error;
      }
    }
  };

  static getData = async (url, options = {}, params = {}) => {
    const cacheKey = getCacheKey(url, params);
    const cached = apiCache.get(cacheKey);
    if (isCacheValid(cached)) {
      return cached.data;
    }
    try {
      let result = await axios.get(APP_URL + url, {
        params,
        headers: { ...options, Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      apiCache.set(cacheKey, { data: result, timestamp: Date.now() });
      return result;
    } catch (error) {
      if (error.response?.status === 401) {
        if (url.includes('/auth') || url.includes('/stores')) {
          localStorage.clear();
          localStorage.setItem("issue", "You are not authorized...");
          window.location.href = "/";
        } else {
          ShowDangerToast("Unauthorized access. Please check your session.");
          throw error;
        }
      } else if (error.response?.status !== 200) {
        ShowDangerToast(error?.response?.data?.error || "Something went wrong!");
        throw error;
      }
    }
  };

  static postData = async (url, data, options = {}) => {
    this.invalidateCache();
    try {
      let result = await axios.post(APP_URL + url, data, {
        headers: { ...options, Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      return result;
    } catch (error) {
      if (error.response?.status === 401 && url === '/auth/login') {
        throw new Error(error.response?.data?.error || 'Invalid email or password');
      }
      if (error.response?.status === 401) {
        if (url.includes('/auth') || url.includes('/stores')) {
          localStorage.clear();
          window.location.href = "/";
        } else {
          ShowDangerToast("Unauthorized access. Please check your session.");
          throw error;
        }
      } else if (error.response?.status !== 200) {
        ShowDangerToast(error?.response?.data?.error || "Something went wrong!");
        throw error;
      }
    }
  };

  static postFormData = async (url, formData, options = {}) => {
    this.invalidateCache();
    try {
      let result = await axios.post(APP_URL + url, formData, {
        headers: {
          ...options,
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      return result;
    } catch (error) {
      if (error.response?.status === 401) {
        if (url.includes('/auth') || url.includes('/stores')) {
          localStorage.clear();
          window.location.href = "/";
        } else {
          ShowDangerToast("Unauthorized access. Please check your session.");
          throw error;
        }
      } else if (error.response?.status !== 200) {
        ShowDangerToast(error?.response?.data?.error || "Something went wrong!");
        throw error;
      }
    }
  };

  static putData = async (url, data, options = {}) => {
    this.invalidateCache();
    try {
      let result = await axios.put(APP_URL + url, data, {
        headers: { ...options, Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      return result;
    } catch (error) {
      if (error.response?.status === 401) {
        if (url.includes('/auth') || url.includes('/stores')) {
          localStorage.clear();
          window.location.href = "/";
        } else {
          ShowDangerToast("Unauthorized access. Please check your session.");
          throw error;
        }
      } else if (error.response?.status !== 200) {
        ShowDangerToast(error?.response?.data?.error || "Something went wrong!");
        throw error;
      }
    }
  };

  static putFormData = async (url, formData, options = {}) => {
    this.invalidateCache();
    try {
      let result = await axios.put(APP_URL + url, formData, {
        headers: {
          ...options,
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      return result;
    } catch (error) {
      console.error('PUT FormData error:', error);
      if (error.response?.status === 401) {
        if (url.includes('/auth') || url.includes('/stores')) {
          localStorage.clear();
          window.location.href = "/";
        } else {
          ShowDangerToast("Unauthorized access. Please check your session.");
          throw error;
        }
      } else if (error.response?.status !== 200) {
        ShowDangerToast(error?.response?.data?.error || "Something went wrong!");
        throw error;
      }
    }
  };

  static deleteData = async (url, options = {}) => {
    this.invalidateCache();
    try {
      let result = await axios.delete(APP_URL + url, {
        headers: { ...options, Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      return result;
    } catch (error) {
      if (error.response?.status === 401) {
        if (url.includes('/auth') || url.includes('/stores')) {
          localStorage.clear();
          window.location.href = "/";
        } else {
          ShowDangerToast("Unauthorized access. Please check your session.");
          throw error;
        }
      } else if (error.response?.status !== 200) {
        ShowDangerToast(error?.response?.data?.error || "Something went wrong!");
        throw error;
      }
    }
  };

  static getDonationRequests = async (storeId, fromDate, toDate) => {
    try {
      let url = `${APP_URL}/donations/${storeId}`;
      const params = [];
      if (fromDate) params.push(`fromDate=${fromDate}`);
      if (toDate) params.push(`toDate=${toDate}`);
      if (params.length) url += `?${params.join("&")}`;
      const response = await axios.get(url);
      return response;
    } catch (error) {
      console.error('Error fetching donation requests:', error);
      ShowDangerToast?.(error?.response?.data?.error || 'Failed to load donation requests.');
      throw error;
    }
  };

  static assignDriver = async (requestId, driverId) => {
    this.invalidateCache();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${APP_URL}/donations/assign-driver`,
        { requestId, driverId },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      return response;
    } catch (error) {
      console.error('Error assigning driver:', error);
      ShowDangerToast?.(error?.response?.data?.error || 'Failed to assign driver.');
      throw error;
    }
  };

  static getDrivers = async (storeId) => {
    const cacheKey = getCacheKey('/drivers', { storeId });
    const cached = apiCache.get(cacheKey);
    if (isCacheValid(cached)) {
      return cached.data.data;
    }
    try {
      const response = await axios.get(`${APP_URL}/drivers`, {
        params: { storeId },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      apiCache.set(cacheKey, { data: response, timestamp: Date.now() });
      return response.data;
    } catch (error) {
      console.error('Error fetching drivers:', error);
      throw error;
    }
  };
}